import React, { useEffect, useRef, useState } from 'react'
import Switch from './switch'

interface RangeInputProps {
  from?: number
  to?: number
  controlRange: (target: 'from' | 'to', value: number | string) => void
  enableSwitch?: boolean
  defaultDisabled?: boolean
  helperText?: string
  singleValue?: boolean
  isEnabled?: boolean
  min?: number
  max?: number
}
const RangeInput: React.FC<RangeInputProps> = ({
  from,
  to,
  controlRange,
  enableSwitch,
  helperText,
  singleValue,
  defaultDisabled,
  isEnabled,
  min = 0,
  max = 100,
}) => {
  const fromSliderRef = useRef<HTMLInputElement>(null)
  const toSliderRef = useRef<HTMLInputElement>(null)

  // Use '||' operator instead of '??' to check for NaN value and provide better default value support
  const [rangeFrom, setRangeFrom] = useState(from ?? min)
  const [rangeTo, setRangeTo] = useState(to ?? max)
  const [enabled, setEnabled] = useState(!defaultDisabled)

  useEffect(() => {
    if (from) {
      setRangeFrom(from)
    }
    if (to) {
      setRangeTo(to)
    }
  }, [from, to])

  // Use controlled state over local state if specified
  const modifiable = isEnabled !== undefined ? isEnabled : enabled

  const controlFromSlider = () => {
    const [newFrom, newTo] = getParsed()

    if (newFrom > newTo) {
      fromSliderRef.current!.value = newTo.toString()
      controlRange('from', newTo)
      setRangeFrom(newTo)
    } else {
      fromSliderRef.current!.value = newFrom.toString()
      controlRange('from', newFrom)
      setRangeFrom(newFrom)
    }
  }

  const controlToSlider = () => {
    const [newFrom, newTo] = getParsed()

    if (newFrom <= newTo) {
      toSliderRef.current!.value = newTo.toString()
      controlRange('to', newTo)
      setRangeTo(newTo)
    } else {
      toSliderRef.current!.value = newFrom.toString()
      controlRange('to', newFrom)
      setRangeTo(newFrom)
    }
  }

  const getParsed = () => {
    const fromValue = parseInt(fromSliderRef.current!.value)
    const toValue = parseInt(toSliderRef.current!.value)
    return [fromValue, toValue]
  }

  useEffect(() => {
    const sliderColor = '#E9EAEB'
    const rangeColor = modifiable ? '#000' : '#A4A8AE'
    const fromColorPercent = ((rangeFrom - min) / (max - min)) * 100
    const toColorPercent = ((rangeTo - min) / (max - min)) * 100

    toSliderRef.current!.style.background = `linear-gradient(
      to right,
      ${sliderColor} 0%,
      ${sliderColor} ${fromColorPercent}%,
      ${rangeColor} ${fromColorPercent}%,
      ${rangeColor} ${toColorPercent}%,
      ${sliderColor} ${toColorPercent}%,
      ${sliderColor} 100%)`
  }, [rangeFrom, rangeTo, modifiable])

  const toggleEnabled = () => {
    if (isEnabled === undefined) {
      setEnabled(!modifiable)
    }
    controlRange('from', !modifiable ? rangeFrom : '')
    controlRange('to', !modifiable ? rangeTo : '')
  }

  useEffect(() => {
    if (!modifiable) {
      setRangeFrom(0)
      setRangeTo(100)
    }
  }, [modifiable])

  return (
    <div className="range_wrapper">
      <div className="range_container">
        <div className="form_control">
          {!singleValue && (
            <div className="form_control_container">
              <span className={`form_control_container__time__input text-label ${!modifiable ? 'text-muted' : ''}`}>
                {rangeFrom}%
              </span>
            </div>
          )}
          <div className="form_control_container">
            <span className={`form_control_container__time__input text-label ${!modifiable ? 'text-muted' : ''}`}>
              {rangeTo}%
            </span>
          </div>
        </div>

        <div className="sliders_control">
          <input
            className={`${singleValue || rangeTo == 0 ? 'hidden' : ''} ${!modifiable ? 'disabled' : ''}`}
            ref={fromSliderRef}
            id="fromSlider"
            type="range"
            value={rangeFrom}
            min={min}
            max={max}
            onChange={controlFromSlider}
            disabled={!modifiable}
          />
          <input
            className={`${rangeFrom == 100 ? 'hidden' : ''} ${!modifiable ? 'disabled' : ''}`}
            ref={toSliderRef}
            type="range"
            value={rangeTo}
            min={min}
            max={max}
            onChange={controlToSlider}
            disabled={!modifiable}
          />
        </div>
      </div>

      {enableSwitch && <Switch enabled={modifiable} onChange={toggleEnabled} />}
      {helperText
        ? // TODO: add helper icon with popover
          ''
        : null}
    </div>
  )
}

export default RangeInput
